import { useDesignSystem } from 'design-system';
import PillCtaButton from 'components/atomic/atoms/pill-cta-button';
import Card, { CARD_VARIANTS } from 'components/atomic/atoms/card';
import FlexContainer from 'components/atomic/atoms/flex-container';
import Text from 'components/atomic/atoms/text';

export interface ConnectionRequestCtaCardStrings {
  bookTourLabel: string;
  requestInfoLabel: string;
  tagline: string;
}

type ConnectionRequestCtaCardProps = {
  strings: ConnectionRequestCtaCardStrings;
  onBookTour: () => void;
  onRequestInfo: () => void;
  heading?: string;
  headerSlot?: React.ReactNode;
};

/**
 * ConnectionRequestCtaCard
 *
 * UI Layer: src/ui
 *
 * Renders the connection request CTA card with book tour and request info buttons.
 * Receives all copy as pre-formatted strings — no i18n, no domain logic.
 */
export function ConnectionRequestCtaCard({
  strings,
  onBookTour,
  onRequestInfo,
  heading,
  headerSlot,
}: ConnectionRequestCtaCardProps) {
  const { theme, tokens: { typography, effects }} = useDesignSystem();

  const pillSx = {
    fontFamily: theme.fonts.heading,
    fontWeight: typography.fontWeight.bold,
  };

  return (
    <Card
      variant={CARD_VARIANTS.SHADOW}
      testId="connection-request-cta-card"
      sx={{
        px: 3,
        py: '29px',
        borderRadius: theme.buttons.borderRadiusModal,
        boxShadow: effects.elevation[200],
      }}
    >
      <FlexContainer direction="column" align="stretch" gap={1.5}>
        {heading && (
          <Text
            component="h3"
            variant="h6"
            sx={{
              fontFamily: theme.fonts.heading,
              fontWeight: typography.fontWeight.bold,
              fontSize: '18px',
              textAlign: 'center',
              mb: 0,
            }}
          >
            {heading}
          </Text>
        )}
        {headerSlot}
        <PillCtaButton
          variant="outline"
          size="lg"
          testId="connection-request-cta-book-tour"
          onClick={onBookTour}
          sx={pillSx}
        >
          {strings.bookTourLabel}
        </PillCtaButton>

        <PillCtaButton
          variant="primary"
          size="lg"
          testId="connection-request-cta-request-info"
          onClick={onRequestInfo}
          sx={pillSx}
        >
          {strings.requestInfoLabel}
        </PillCtaButton>

        {strings.tagline && (
          <Text
            component="p"
            variant="body2"
            sx={{
              fontFamily: theme.fonts.text,
              fontSize: typography.fontSize.md,
              lineHeight: typography.lineHeight.loose,
              color: theme.colors.primaryDark,
              textAlign: 'center',
            }}
          >
            {strings.tagline}
          </Text>
        )}
      </FlexContainer>
    </Card>
  );
}
